/**
 * B3 (vague Fluidité) — Page « Vos actions » (`/vos-actions/:token`), sans mot de passe.
 *
 * Ouverte depuis l'e-mail reçu par l'entreprise (avec ou sans compte) : elle ne montre
 * que SES actions sur CE chantier. Trois réponses, en gros boutons pour le téléphone :
 * - « Fait, avec photo » : photo (appareil photo du téléphone) et/ou commentaire ; l'action
 *   passe « À vérifier » chez l'architecte ;
 * - « Pas d'accord » : motif ; acte engageant, confirmé par un code (SMS ou e-mail) ;
 * - « Question » : texte libre.
 * La réponse s'attache à l'action et apparaît chez l'architecte (Ma semaine et Fil).
 *
 * API : GET /api/vos-actions/{token}, POST …/actions/{id}/fait | desaccord | question,
 *       POST …/code (code de vérification).
 */
import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Camera, CheckCircle, ChatCircleText, HandPalm, Warning, Clock } from '@phosphor-icons/react';
import CodeVerificationDialog, { CODE_HEADER, verificationRequise } from '../components/shared/CodeVerificationDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COULEUR_ETAT = {
  actif: { bg: '#FFF4E5', fg: '#9A5B13' },
  a_verifier: { bg: '#EAF2FB', fg: '#1E3A5F' },
  fait: { bg: '#E8F3EC', fg: '#2D6A4F' },
};

const BOUTON = 'w-full min-h-[48px] px-4 text-base font-semibold rounded-lg flex items-center justify-center gap-2 disabled:opacity-50';

function dateHeure(v) {
  if (!v) return '';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
}

function CarteAction({ action, token, onMaj, surligner }) {
  const [mode, setMode] = useState(null); // fait | desaccord | question
  const [texte, setTexte] = useState('');
  const [photo, setPhoto] = useState(null);
  const [apercu, setApercu] = useState(null);
  const [envoi, setEnvoi] = useState(false);
  const [codeOpen, setCodeOpen] = useState(false);
  const fileRef = useRef(null);
  const base = `${API_URL}/api/vos-actions/${token}/actions/${action.id}`;
  const couleur = COULEUR_ETAT[action.etat] || COULEUR_ETAT.actif;

  const ouvrir = (m) => {
    setMode(mode === m ? null : m);
    setTexte('');
    setPhoto(null);
    setApercu(null);
    if (m === 'fait') setTimeout(() => fileRef.current?.click(), 150);
  };

  const fin = (res, message) => {
    toast.success(res?.data?.message || message);
    setMode(null);
    setTexte('');
    setPhoto(null);
    setApercu(null);
    onMaj();
  };

  const envoyerFait = async () => {
    if (!photo && !texte.trim()) {
      toast.error('Ajoutez une photo ou un commentaire.');
      return;
    }
    setEnvoi(true);
    try {
      const fd = new FormData();
      if (photo) fd.append('photo', photo, photo.name || 'photo.jpg');
      fd.append('commentaire', texte.trim());
      const res = await axios.post(`${base}/fait`, fd);
      fin(res, "Envoyé à l'architecte pour vérification.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "L'envoi n'a pas abouti. Réessayez.");
    } finally {
      setEnvoi(false);
    }
  };

  const envoyerDesaccord = async (code) => {
    const headers = code ? { [CODE_HEADER]: code } : {};
    const res = await axios.post(`${base}/desaccord`, { motif: texte.trim() }, { headers });
    fin(res, "Votre désaccord a été transmis à l'architecte.");
  };

  const soumettreDesaccord = async () => {
    if (texte.trim().length < 3) {
      toast.error('Indiquez le motif de votre désaccord.');
      return;
    }
    setEnvoi(true);
    try {
      await envoyerDesaccord(null);
    } catch (e) {
      if (verificationRequise(e)) setCodeOpen(true);
      else toast.error(e?.response?.data?.detail || "L'envoi n'a pas abouti. Réessayez.");
    } finally {
      setEnvoi(false);
    }
  };

  const envoyerQuestion = async () => {
    if (texte.trim().length < 3) {
      toast.error('Écrivez votre question.');
      return;
    }
    setEnvoi(true);
    try {
      const res = await axios.post(`${base}/question`, { texte: texte.trim() });
      fin(res, "Votre question a été transmise à l'architecte.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "L'envoi n'a pas abouti. Réessayez.");
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <article
      id={`action-${action.id}`}
      className={`bg-white rounded-xl border p-4 space-y-3 ${surligner ? 'border-[#C4773B] ring-2 ring-[#C4773B]/30' : 'border-[#E0E0DB]'}`}
      data-testid={`vos-actions-carte-${action.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-wide text-[#6B6B6B]">{action.type_libelle}{action.lot ? ` · Lot ${action.lot}` : ''}</p>
          <h2 className="text-lg font-semibold text-[#2C2C2C] leading-snug">{action.titre}</h2>
        </div>
        <span className="shrink-0 text-xs font-semibold px-2 py-1 rounded" style={{ background: couleur.bg, color: couleur.fg }}>
          {action.etat_libelle}
        </span>
      </div>
      {action.description && action.description !== action.titre && (
        <p className="text-sm text-[#4A4A4A] whitespace-pre-line">{action.description}</p>
      )}
      <div className="flex flex-wrap gap-2 text-sm">
        {action.urgent && <span className="px-2 py-0.5 rounded bg-[#FDECEC] text-[#B42318] font-semibold">Urgent</span>}
        {action.echeance_fr && (
          <span className={`px-2 py-0.5 rounded ${action.en_retard ? 'bg-[#FDECEC] text-[#B42318] font-semibold' : 'bg-[#F5F5F3] text-[#4A4A4A]'}`}>
            <Clock size={14} className="inline mr-1" />
            {action.en_retard ? `En retard — échéance le ${action.echeance_fr}` : `Échéance le ${action.echeance_fr}`}
          </span>
        )}
        {action.lieu && <span className="px-2 py-0.5 rounded bg-[#F5F5F3] text-[#4A4A4A]">{action.lieu}</span>}
      </div>
      {action.photos.length > 0 && (
        <div className="flex gap-2 overflow-x-auto">
          {action.photos.map((p) => (
            <a key={p} href={`${API_URL}${p}`} target="_blank" rel="noopener noreferrer">
              <img src={`${API_URL}${p}`} alt="Photo jointe à l'action" className="h-24 w-24 object-cover rounded-lg border border-[#E0E0DB]" />
            </a>
          ))}
        </div>
      )}
      {action.levee_refusee_motif && (
        <p className="text-sm text-[#8B2C2C] bg-[#FDF2F2] rounded p-2">Levée refusée : {action.levee_refusee_motif}</p>
      )}
      {action.reponses.length > 0 && (
        <ul className="space-y-1 text-sm border-t border-[#F0F0EC] pt-2">
          {action.reponses.map((r, i) => (
            <li key={`${r.at}-${i}`} className="text-[#4A4A4A]">
              <span className="font-semibold">{r.libelle}</span>
              {r.at ? ` (${dateHeure(r.at)})` : ''}{r.texte ? ` : ${r.texte}` : ''}
              {r.reponse_moe && <span className="block pl-3 text-[#1E3A5F]">Réponse de l'architecte : {r.reponse_moe}</span>}
            </li>
          ))}
        </ul>
      )}
      {action.balle && action.etat !== 'actif' && (
        <p className="text-sm text-[#1E3A5F]" data-testid="vos-actions-attente">En attente de l'architecte.</p>
      )}

      {action.etat === 'actif' && (
        <div className="space-y-2 pt-1">
          <div className="grid grid-cols-1 gap-2">
            <button
              type="button"
              className={`${BOUTON} bg-[#2D6A4F] text-white`}
              onClick={() => ouvrir('fait')}
              data-testid="vos-actions-fait"
            >
              <Camera size={22} /> Fait, avec photo
            </button>
            <div className="grid grid-cols-2 gap-2">
              <button type="button" className={`${BOUTON} bg-white border border-[#8B3A3A] text-[#8B3A3A]`}
                onClick={() => ouvrir('desaccord')} data-testid="vos-actions-desaccord">
                <HandPalm size={20} /> Pas d'accord
              </button>
              <button type="button" className={`${BOUTON} bg-white border border-[#1E3A5F] text-[#1E3A5F]`}
                onClick={() => ouvrir('question')} data-testid="vos-actions-question">
                <ChatCircleText size={20} /> Question
              </button>
            </div>
          </div>

          {mode === 'fait' && (
            <div className="rounded-lg bg-[#F5F9F6] p-3 space-y-2">
              <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = '';
                  if (!f) return;
                  if (f.size > 10 * 1024 * 1024) { toast.error('Photo trop volumineuse (10 Mo au plus).'); return; }
                  setPhoto(f);
                  setApercu(URL.createObjectURL(f));
                }} />
              {apercu ? (
                <img src={apercu} alt="Photo de la réalisation" className="w-full max-h-64 object-cover rounded-lg" />
              ) : (
                <button type="button" className={`${BOUTON} bg-white border border-[#2D6A4F] text-[#2D6A4F]`}
                  onClick={() => fileRef.current?.click()}>
                  <Camera size={20} /> Prendre une photo
                </button>
              )}
              <textarea value={texte} onChange={(e) => setTexte(e.target.value)} rows={2}
                placeholder="Commentaire (facultatif si vous joignez une photo)"
                className="w-full rounded-lg border border-[#E0E0DB] p-3 text-base" />
              <button type="button" className={`${BOUTON} bg-[#2D6A4F] text-white`} onClick={envoyerFait} disabled={envoi}
                data-testid="vos-actions-fait-envoyer">
                <CheckCircle size={20} /> {envoi ? 'Envoi…' : "Envoyer à l'architecte"}
              </button>
            </div>
          )}
          {mode === 'desaccord' && (
            <div className="rounded-lg bg-[#FDF6F6] p-3 space-y-2">
              <textarea value={texte} onChange={(e) => setTexte(e.target.value)} rows={3} autoFocus
                placeholder="Pourquoi n'êtes-vous pas d'accord ?"
                className="w-full rounded-lg border border-[#E0E0DB] p-3 text-base" data-testid="vos-actions-desaccord-motif" />
              <p className="text-xs text-[#6B6B6B]">Un code de confirmation vous sera envoyé.</p>
              <button type="button" className={`${BOUTON} bg-[#8B3A3A] text-white`} onClick={soumettreDesaccord} disabled={envoi}>
                {envoi ? 'Envoi…' : 'Envoyer mon désaccord'}
              </button>
            </div>
          )}
          {mode === 'question' && (
            <div className="rounded-lg bg-[#F3F6FA] p-3 space-y-2">
              <textarea value={texte} onChange={(e) => setTexte(e.target.value)} rows={3} autoFocus
                placeholder="Votre question à l'architecte"
                className="w-full rounded-lg border border-[#E0E0DB] p-3 text-base" />
              <button type="button" className={`${BOUTON} bg-[#1E3A5F] text-white`} onClick={envoyerQuestion} disabled={envoi}>
                {envoi ? 'Envoi…' : 'Envoyer ma question'}
              </button>
            </div>
          )}
        </div>
      )}
      <CodeVerificationDialog
        open={codeOpen}
        onOpenChange={setCodeOpen}
        demanderUrl={`${API_URL}/api/vos-actions/${token}/code`}
        demanderBody={{ action: 'desaccord_action' }}
        titre="Confirmer votre désaccord"
        onValider={(code) => envoyerDesaccord(code)}
      />
    </article>
  );
}

export default function VosActionsPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [chargement, setChargement] = useState(true);
  const cible = (typeof window !== 'undefined' && window.location.hash.startsWith('#action-'))
    ? window.location.hash.slice(8) : null;

  const charger = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/vos-actions/${token}`);
      setData(res.data);
    } catch (e) {
      const d = e?.response?.data?.detail;
      setErreur(typeof d === 'string' ? d : "Ce lien n'est pas valide.");
    } finally {
      setChargement(false);
    }
  };

  useEffect(() => { charger(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (data && cible) document.getElementById(`action-${cible}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [data, cible]);

  if (chargement) {
    return <div className="min-h-screen flex items-center justify-center bg-[#F8F7F4] text-[#6B6B6B]">Chargement…</div>;
  }
  if (erreur) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F7F4] p-6">
        <div className="bg-white rounded-xl border border-[#E0E0DB] p-6 max-w-md text-center space-y-2">
          <Warning size={32} className="mx-auto text-[#C4773B]" />
          <p className="text-[#2C2C2C]">{erreur}</p>
          <p className="text-sm text-[#6B6B6B]">Demandez un nouveau lien à l'architecte du chantier.</p>
        </div>
      </div>
    );
  }
  const actions = data?.actions || [];
  const aFaire = actions.filter((a) => a.etat === 'actif');
  const autres = actions.filter((a) => a.etat !== 'actif');
  return (
    <div className="min-h-screen bg-[#F8F7F4]" data-testid="vos-actions-page">
      <header className="bg-[#1E3A5F] text-white px-4 py-4">
        <div className="max-w-xl mx-auto">
          {data.cabinet && <p className="text-xs uppercase tracking-wider opacity-80">{data.cabinet}</p>}
          <h1 className="text-2xl" style={{ fontFamily: 'DM Serif Display, serif' }}>Vos actions</h1>
          <p className="text-sm opacity-90">{data.entreprise} · {data.chantier?.nom}</p>
        </div>
      </header>
      <main className="max-w-xl mx-auto px-4 py-4 space-y-4">
        <p className="text-sm text-[#4A4A4A]" data-testid="vos-actions-resume">
          {aFaire.length === 0 ? 'Aucune action à faire pour le moment.'
            : `${aFaire.length} action${aFaire.length > 1 ? 's' : ''} à faire.`}
        </p>
        {aFaire.map((a) => <CarteAction key={a.id} action={a} token={token} onMaj={charger} surligner={a.id === cible} />)}
        {autres.length > 0 && <h2 className="text-sm font-semibold text-[#6B6B6B] uppercase tracking-wide pt-2">Déjà envoyées ou levées</h2>}
        {autres.map((a) => <CarteAction key={a.id} action={a} token={token} onMaj={charger} surligner={a.id === cible} />)}
        <p className="text-xs text-[#9A9A9A] text-center pt-4">
          Page personnelle, sans mot de passe, limitée à vos actions sur ce chantier. Ne transférez pas ce lien.
        </p>
      </main>
    </div>
  );
}
